import { useEffect, useState } from "preact/hooks"
import { useLocation } from "wouter-preact"
import { useSearch } from "wouter-preact/use-location"
import { Calendar, Kanban, List, Plus, TrendingUp, Wand2 } from "lucide-react"
import { Button } from "../../../components/ui/button"
import { classes } from "../../../utils"
import { getYear } from 'date-fns'

// Components
import { CalendarList } from './components/calendar_list'
import { PromotionalCalendar } from './components/promotional_calendar'
import { AccrualSummaryView } from './accrual_summary_view'
import { TradeRateSummaryView } from './trade_rate_summary_view'
import { MonthEndReportView } from './month_end_report_view'
import { EMPTY_PROMOTION_FILTERS, PromotionFilterSelection } from './promotion_filters'
import { PromotionPlanningBoard } from './components/promotion_planning_board'
import { CREATE_PROMOTION_PATH, MONTH_END_ACCRUAL_REPORT_PATH, PROMOTION_ACCRUALS_PATH, TRADE_PROMOTION_MANAGEMENT_PATH, TRADE_RATE_PATH } from "../routes"

// Types
import type { CalendarPromotionLoadState } from "../promo_creation/hooks/use_promotional_context_api"
import type { CalendarFormData } from "../promo_creation/components/calendar_forms"
import { CalendarInfo, Promotion, PromotionStage } from '../promo_creation/types'

export type CalendarViewMode = 'board' | 'list' | 'calendar' | 'accrual'
export type AccrualTab = 'summary' | 'month_end' | 'trade_rate'

interface trade_promotion_route_state {
  viewMode?: CalendarViewMode
  accrualTab?: AccrualTab
}

function normalize_search(search: string): string {
  return search.startsWith("?") ? search : `?${search}`
}

export function route_state_from_url(pathname: string, search: string): trade_promotion_route_state {
  if (pathname === MONTH_END_ACCRUAL_REPORT_PATH) {
    return { viewMode: "accrual", accrualTab: "month_end" }
  }
  if (pathname === PROMOTION_ACCRUALS_PATH) {
    return { viewMode: "accrual", accrualTab: "summary" }
  }
  if (pathname === TRADE_RATE_PATH) {
    return { viewMode: "accrual", accrualTab: "trade_rate" }
  }

  const params = new URLSearchParams(normalize_search(search))
  const view = params.get("view")
  const report = params.get("report") ?? params.get("tab")

  if (view === "board" || view === "list" || view === "calendar") {
    return { viewMode: view }
  }
  if (view === "accrual") {
    return {
      viewMode: "accrual",
      accrualTab:
        report === "month_end" || report === "month-end-report"
          ? "month_end"
          : report === "trade_rate" || report === "trade-rate"
            ? "trade_rate"
            : "summary",
    }
  }
  if (report === "month_end" || report === "month-end-report") {
    return { viewMode: "accrual", accrualTab: "month_end" }
  }

  return {}
}

export function build_trade_promotion_url(pathname: string, search: string): string {
  const params = new URLSearchParams(normalize_search(search))
  params.delete("view")
  params.delete("tab")
  params.delete("report")

  if (pathname !== MONTH_END_ACCRUAL_REPORT_PATH) {
    params.delete("year")
    params.delete("month")
  }

  const query = params.toString()
  return `${pathname}${query ? `?${query}` : ""}`
}

interface CalendarListViewProps {
  className?: string
  calendars: CalendarInfo[]
  promotion_load_by_calendar: Record<string, CalendarPromotionLoadState>
  is_loading_calendars: boolean
  calendars_load_error: string | null
  onEditPromotion: (promotion: Promotion) => void
  onUpdatePromotion: (promotion: Promotion) => void
  onDeletePromotion: (promotionId: string) => void
  onCreatePromotion: (calendarId: string, promotion: Promotion) => Promise<boolean>
  onUpdateCalendar: (calendarId: string, calendarData: CalendarFormData) => Promise<boolean>
  isUpdatingCalendar: boolean
  onUpdatePromotionStage: (promotionId: string, stage: PromotionStage) => Promise<boolean>
  onDuplicatePromotion: (promotionId: string, options: any) => Promise<boolean>
  onCopyForward: (options: any) => Promise<Promotion[] | null>
  onSplitFixedFee: (promotionId: string) => Promise<boolean>
  onProvideFeedback: (calendar: CalendarInfo, feedback: string) => Promise<boolean>
  isProvidingFeedback: boolean
  isAddingPromotion: boolean
  onOpenTradeWiz: () => void
  /** Hydrate promotions for the calendars matching a given year (year-scoped calendar view). */
  hydrateCalendarsForYear: (year: number) => void
  /** Hydrate promotions for every calendar (List and Board views span all years). */
  hydrateAllCalendars: () => void
}

export function CalendarListView({ 
  className,
  calendars,
  promotion_load_by_calendar,
  is_loading_calendars,
  calendars_load_error,
  onEditPromotion,
  onUpdatePromotion,
  onDeletePromotion,
  onCreatePromotion,
  onUpdateCalendar,
  isUpdatingCalendar,
  onUpdatePromotionStage,
  onDuplicatePromotion,
  onCopyForward,
  onSplitFixedFee,
  onProvideFeedback,
  isProvidingFeedback,
  isAddingPromotion,
  onOpenTradeWiz,
  hydrateCalendarsForYear,
  hydrateAllCalendars,
}: CalendarListViewProps) {
  const [location, setLocation] = useLocation()
  const search = useSearch()
  const initial_route_state = route_state_from_url(location, search)
  const metadata_loading_banner = is_loading_calendars && calendars.length === 0
  const promotions_hydrating_initial = calendars.some(
    (calendar_row) => promotion_load_by_calendar[calendar_row.id]?.loading_initial,
  )
  const promotions_loading_additional_pages = calendars.some(
    (calendar_row) => promotion_load_by_calendar[calendar_row.id]?.loading_more,
  )

  // UI state
  const [openCalendarId, setOpenCalendarId] = useState<string | null>(null)
  const [feedback, setFeedback] = useState("")
  const [addingPromotionToCalendar, setAddingPromotionToCalendar] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<CalendarViewMode>(initial_route_state.viewMode ?? 'calendar')
  const [accrualTab, setAccrualTab] = useState<AccrualTab>(initial_route_state.accrualTab ?? 'summary')
  const [selectedYear, setSelectedYear] = useState<number>(getYear(new Date()))
  const [searchTerm, setSearchTerm] = useState("")
  // Chip filters are shared by the Calendar and Accrual views, so a bucket filtered on one reads
  // the same on the other.
  const [promotionFilters, setPromotionFilters] = useState<PromotionFilterSelection>(EMPTY_PROMOTION_FILTERS)
  const [previousCalendarCount, setPreviousCalendarCount] = useState(0)

  useEffect(() => {
    const next_route_state = route_state_from_url(location, search)
    if (next_route_state.viewMode && next_route_state.viewMode !== viewMode) {
      setViewMode(next_route_state.viewMode)
    }
    if (next_route_state.accrualTab && next_route_state.accrualTab !== accrualTab) {
      setAccrualTab(next_route_state.accrualTab)
    }
  }, [location, search])
  
  // Hydrate promotions on demand based on the active view:
  // - Calendar view is year-scoped, so only hydrate the selected year's calendars — unless the
  //   user is searching, since search spans every year.
  // - List and Board views span all years, so hydrate everything when opened.
  // - Accrual view has its own aggregate endpoint and needs no promotion hydration.
  useEffect(() => {
    if (calendars.length === 0) return
    if (viewMode === "accrual") return
    if (viewMode === "list" || viewMode === "board" || searchTerm) {
      hydrateAllCalendars()
    } else {
      hydrateCalendarsForYear(selectedYear)
    }
  }, [viewMode, selectedYear, calendars.length, searchTerm])

  // Effect to detect when a new calendar is added and auto-open it
  useEffect(() => {
    if (calendars.length > previousCalendarCount && previousCalendarCount > 0) {
      // A new calendar was added
      const firstCalendar = calendars[0]
      setOpenCalendarId(firstCalendar.id)
      
      // Set year to match the first calendar if it has one
      if (firstCalendar.calendar_year) {
        setSelectedYear(firstCalendar.calendar_year)
      }
      
      // Switch to list view and scroll to top
      setViewMode('list')
      
      // Scroll to top after a brief delay
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 100)
    }
    
    // Update the previous count
    setPreviousCalendarCount(calendars.length)
  }, [calendars, previousCalendarCount])

  // Enhanced handlers that manage UI state
  async function handleProvideFeedback(calendar: CalendarInfo) {
    const success = await onProvideFeedback(calendar, feedback)
    if (success) {
      setFeedback("")
    }
  }

  async function handleCreatePromotion(calendarId: string, promotion: any) {
    const success = await onCreatePromotion(calendarId, promotion)
    if (success) {
      setAddingPromotionToCalendar(null)
    }
  }

  function navigate_to_view(nextViewMode: CalendarViewMode) {
    setViewMode(nextViewMode)
    if (nextViewMode === "accrual") {
      setAccrualTab("summary")
      setLocation(build_trade_promotion_url(PROMOTION_ACCRUALS_PATH, search), { replace: true })
    } else {
      setLocation(build_trade_promotion_url(TRADE_PROMOTION_MANAGEMENT_PATH, search), { replace: true })
    }
  }

  function navigate_to_accrual_tab(nextAccrualTab: AccrualTab) {
    setAccrualTab(nextAccrualTab)
    const pathname =
      nextAccrualTab === "month_end" ? MONTH_END_ACCRUAL_REPORT_PATH
      : nextAccrualTab === "trade_rate" ? TRADE_RATE_PATH
      : PROMOTION_ACCRUALS_PATH
    setLocation(build_trade_promotion_url(pathname, search), { replace: true })
  }

  return (
    <div class={classes("", className)}>
      {/* Single Condensed Header Row with All Controls */}
      <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div class="flex flex-wrap items-center gap-3">
          <h3 class="text-lg font-medium text-gray-900">
            <span class="hidden sm:inline">Trade Promotion Management</span>
            <span class="sm:hidden">TPM</span>
          </h3>

          <div class="flex flex-wrap gap-2">
            <Button 
              onClick={() => navigate_to_view('board')} 
              variant={viewMode === 'board' ? 'default' : 'outline'}
            >
              <Kanban className="h-4 w-4 mr-2" />
              Plan
            </Button>
            <Button 
              onClick={() => navigate_to_view('list')} 
              variant={viewMode === 'list' ? 'default' : 'outline'}
            >
              <List className="h-4 w-4 mr-2" />
              List View
            </Button>
            <Button 
              onClick={() => navigate_to_view('calendar')} 
              variant={viewMode === 'calendar' ? 'default' : 'outline'}
            >
              <Calendar className="h-4 w-4 mr-2" />
              Calendar View
            </Button>
            <Button
              onClick={() => navigate_to_view('accrual')}
              variant={viewMode === 'accrual' ? 'default' : 'outline'}
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              Accrual
            </Button>
          </div>
        </div>
        
        <div class="flex flex-wrap items-center gap-4">
          {/* Vertical Divider */}
          <div class="w-px h-8 bg-gray-300"></div>
          
          {/* Year Selection */}
          <div class="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setSelectedYear(prev => prev - 1)}
            >
              {selectedYear - 1}
            </Button>
            <Button
              variant="default"
            >
              {selectedYear}
            </Button>
            <Button
              variant="outline"
              onClick={() => setSelectedYear(prev => prev + 1)}
            >
              {selectedYear + 1}
            </Button>
          </div>
          
          {/* Vertical Divider */}
          <div class="w-px h-8 bg-gray-300"></div>
          
          <Button
            onClick={() => setLocation(CREATE_PROMOTION_PATH)}
            variant="outline"
            title="Create a promotion or calendar, copy promotions forward, or upload a calendar"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create
          </Button>

          <div class="w-px h-8 bg-gray-300"></div>

          <Button onClick={onOpenTradeWiz} variant="outline">
            <Wand2 className="h-4 w-4 mr-2" />
            TradeWiz
          </Button>
        </div>
      </div>

      {calendars_load_error !== null ? (
        <div class="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-900">
          {calendars_load_error}
        </div>
      ) : null}

      {metadata_loading_banner ? (
        <div class="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-900 animate-pulse">
          Loading calendars…
        </div>
      ) : promotions_hydrating_initial ? (
        <div class="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-900">
          Loading promotions…
          {promotions_loading_additional_pages ? (
            <span class="block text-xs text-blue-800 mt-0.5">
              Loading additional promotion pages — your calendar fills in progressively.
            </span>
          ) : null}
        </div>
      ) : promotions_loading_additional_pages ? (
        <div class="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-xs text-gray-600">
          Loading more promotions in the background…
        </div>
      ) : null}

      {viewMode === 'board' && (
        <PromotionPlanningBoard
          calendars={calendars}
          onEditPromotion={onEditPromotion}
          onUpdateStage={onUpdatePromotionStage}
          onDuplicatePromotion={onDuplicatePromotion}
          onCopyForward={onCopyForward}
          onSplitFixedFee={onSplitFixedFee}
          promotion_load_by_calendar={promotion_load_by_calendar}
        />
      )}
      {viewMode === 'calendar' && (
        <PromotionalCalendar 
          calendars={calendars}
          promotion_load_by_calendar={promotion_load_by_calendar}
          onEditPromotion={onEditPromotion}
          onDeletePromotion={onDeletePromotion}
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          filters={promotionFilters}
          onFiltersChange={setPromotionFilters}
        />
      )}
      {viewMode === 'list' && (
        <CalendarList
          calendars={calendars}
          is_loading_calendars={is_loading_calendars}
          calendars_load_error={calendars_load_error}
          promotion_load_by_calendar={promotion_load_by_calendar}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          openCalendarId={openCalendarId}
          setOpenCalendarId={setOpenCalendarId}
          onUpdatePromotion={onUpdatePromotion}
          onDeletePromotion={onDeletePromotion}
          onCreatePromotion={handleCreatePromotion}
          onUpdateCalendar={onUpdateCalendar}
          isUpdatingCalendar={isUpdatingCalendar}
          feedback={feedback}
          onFeedbackChange={setFeedback}
          onProvideFeedback={handleProvideFeedback}
          isProvidingFeedback={isProvidingFeedback}
          isAddingPromotion={isAddingPromotion}
          addingPromotionToCalendar={addingPromotionToCalendar}
          setAddingPromotionToCalendar={setAddingPromotionToCalendar}
        />
      )}
      {viewMode === 'accrual' && (
        <div class="space-y-4">
          <div class="flex gap-2">
            <Button
              onClick={() => navigate_to_accrual_tab('summary')}
              variant={accrualTab === 'summary' ? 'default' : 'outline'}
              size="sm"
            >
              Promotion Accruals
            </Button>
            <Button
              onClick={() => navigate_to_accrual_tab('month_end')}
              variant={accrualTab === 'month_end' ? 'default' : 'outline'}
              size="sm"
            >
              Month-End Report
            </Button>
            <Button
              onClick={() => navigate_to_accrual_tab('trade_rate')}
              variant={accrualTab === 'trade_rate' ? 'default' : 'outline'}
              size="sm"
            >
              Trade Rate
            </Button>
          </div>
          {accrualTab === 'summary' && (
            <AccrualSummaryView
              selectedYear={selectedYear}
              onEditPromotion={onEditPromotion}
              filters={promotionFilters}
              onFiltersChange={setPromotionFilters}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
            />
          )}
          {accrualTab === 'month_end' && <MonthEndReportView />}
          {accrualTab === 'trade_rate' && <TradeRateSummaryView selectedYear={selectedYear} />}
        </div>
      )}
    </div>
  )
}
